// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to update person details in the remote API.
 */
import * as z from 'zod';
import { personSchema } from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating and transforming the API response when updating a person.
 * Restructures the response to expose the person directly.
 */
const updatePersonResponseSchema = z
  .object({ data: personSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, person: data }));

/** Type representing the validated response from updating a person */
type UpdatePersonResponse = z.infer<typeof updatePersonResponseSchema>;

/**
 * Interface defining the parameters that can be updated for a person.
 * All fields are optional to allow partial updates.
 * @property firstName - The first name of the person.
 * @property lastName - The last name of the person.
 */
export interface UpdatePersonParameters {
  firstName?: string;
  lastName?: string;
}

/**
 * Updates a person record in the remote API.
 *
 * @param key - The key of the person to update.
 * @param parameters - The parameters to update, which can include firstName and lastName.
 * @returns A promise that resolves to the validated response containing the updated person.
 * @throws Will throw if the API request fails or if the response fails validation.
 */
export async function updatePerson(
  key: string,
  parameters: UpdatePersonParameters
): Promise<UpdatePersonResponse> {
  const url = `/v1/person/${key}`;
  const response = await api.patch(url, parameters);

  return parseResponse(updatePersonResponseSchema, response.data, {
    schemaName: 'UpdatePerson',
    endpoint: url,
  });
}
